import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateAgentMemoryEntry } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { AgentMemoryEntryType } from '@/types'
import { Check, X } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

const CONTENT_MAX = 4000

const MEMORY_TYPE_LABELS: Record<AgentMemoryEntryType, string> = {
  Fact: 'Fact',
  Feedback: 'Feedback',
}

interface CreateMemoryDialogFormValues {
  type: AgentMemoryEntryType | ''
  content: string
}

interface CreateMemoryDialogProps {
  projectId: string
  agentId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateMemoryDialog({ projectId, agentId, open, onOpenChange }: CreateMemoryDialogProps) {
  const createMemory = useCreateAgentMemoryEntry(projectId, agentId)
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<CreateMemoryDialogFormValues>({
    defaultValues: { type: '', content: '' },
  })

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset()
    onOpenChange(isOpen)
  }

  const onSubmit = async (data: CreateMemoryDialogFormValues) => {
    if (!data.type) return
    try {
      await createMemory.mutateAsync({
        type: data.type,
        content: data.content.trim(),
      })
      toast.success('Memory entry created')
      handleClose(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create memory entry')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[560px] gap-6 rounded-2xl border-none p-8 shadow-elevated" hideCloseButton>
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold text-foreground">Create Memory</DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="create-memory-type" className={labelClass}>Type</Label>
            <Controller
              name="type"
              control={control}
              rules={{ required: 'Type is required' }}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger id="create-memory-type" aria-label="Memory type" className="h-10 rounded-lg">
                    <SelectValue placeholder="Select a type" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEMORY_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.type && <p className="text-sm text-destructive" role="alert">{errors.type.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-memory-content" className={labelClass}>
              Memory description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="create-memory-content"
              placeholder="Multiline text"
              {...register('content', {
                validate: v => v.trim().length > 0 || 'Memory content is required',
                maxLength: { value: CONTENT_MAX, message: `Content must not exceed ${CONTENT_MAX} characters` },
              })}
              rows={3}
              className="rounded-lg"
            />
            {errors.content && <p className="text-sm text-destructive" role="alert">{errors.content.message}</p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)} className="h-10 rounded-lg px-4 font-bold">
              Cancel
            </Button>
            <Button type="submit" disabled={createMemory.isPending} className="h-10 rounded-lg px-4 font-bold">
              <Check className="h-5 w-5" />
              {createMemory.isPending ? 'Creating...' : 'Create Memory'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
